<script lang="ts" setup>
  const props = withDefaults(
    defineProps<{
      block?: boolean
      disabled?: boolean
      expand?: 'block' | 'full'
      icon?: string
      label: string
      labelRhs?: string
      showIconLhs?: boolean
      showIconRhs?: boolean
      size?: 'extra-small' | 'small' | 'medium' | 'large'
      shouldShowLoadingSpinner?: boolean
      shouldShowLoadingAnimation?: boolean
      pill?: boolean
      spinnerColor?: string
      animationColor?: 'light' | 'dark' | 'red'
      variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'dark-primary' | 'dark-secondary'
      color?: string
      wrap?: boolean
    }>(),
    {
      block: false,
      pill: false,
      disabled: false,
      expand: undefined,
      icon: undefined,
      labelRhs: undefined,
      showIconLhs: false,
      showIconRhs: false,
      shouldShowLoadingSpinner: false,
      shouldShowLoadingAnimation: false,
      size: 'medium',
      spinnerColor: 'var(--color-base-paper)',
      animationColor: 'light',
      variant: 'primary',
      color: 'var(--color-base-red)',
      wrap: false
    }
  )

  const sizeClass = computed(() => (props.size ? `button-${props.size}` : 'button-medium'))
  const wrapClass = computed(() => (props.wrap ? 'ion-text-wrap' : 'ion-text-nowrap'))

  const expandSize = computed(() => {
    if (props.block || props.labelRhs) {
      return 'block'
    } else if (props.expand) {
      return props.expand
    } else {
      return undefined
    }
  })

  const colorGhost = computed(() => {
    if (props.color) {
      return props.color
    } else {
      return 'var(--color-base-red)'
    }
  })

  // TODO: add other variants from Figma + hover/focus/active/disabled states
</script>

<template>
  <ion-button
    :class="['g-button', variant, sizeClass, wrapClass, { 'button-pill': pill }]"
    :disabled="disabled || shouldShowLoadingSpinner || shouldShowLoadingAnimation"
    :expand="expandSize"
    :fill="variant !== 'primary' ? 'outline' : 'solid'"
  >
    <GLoadingSpinner v-if="shouldShowLoadingSpinner" slot="start" :color="spinnerColor" class="px-2" />
    <GLoadingAnimation v-else-if="shouldShowLoadingAnimation" slot="start" :color="animationColor" />
    <ion-icon
      v-else-if="showIconLhs && icon && !(shouldShowLoadingSpinner || shouldShowLoadingAnimation)"
      slot="start"
      :icon="icon"
    />
    <div :class="labelRhs && 'button-label-row'">
      <span>{{ label }}</span>
      <span v-if="labelRhs">{{ labelRhs }}</span>
    </div>
    <ion-icon v-if="showIconRhs && icon" slot="end" :icon="icon" />
  </ion-button>
</template>

<style lang="scss" scoped>
  :deep(.button-native) {
    background: var(--text-link-link-disabled) !important;
  }

  ion-button {
    --box-shadow: none;
    --border-radius: 4px;
    --padding-start: 24px;
    --padding-end: 24px;
  }

  .g-button {
    cursor: pointer;
  }

  .button-pill {
    --border-radius: 20px;
  }

  .primary {
    &.button-disabled {
      opacity: 1;
      --background: var(--color-text-muted);
      color: white !important;
    }
  }

  .button-label-row {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .button-extra-small {
    font-size: 12px;
    height: 30px;
    --padding-top: 12px;
    --padding-bottom: 12px;
  }

  .button-small {
    font-size: 14px;
    height: 40px;
    --padding-top: 12px;
    --padding-bottom: 12px;
  }

  .button-medium {
    font-size: 16px;
    height: 48px;
    --padding-top: 16px;
    --padding-bottom: 16px;
  }

  .button-large {
    font-size: 18px;
    height: 64px;
    --padding-top: 24px;
    --padding-bottom: 24px;
  }

  .secondary {
    --background: var(--color-base-white);
    --border-color: var(--color-base-red);
    color: var(--color-base-red);
    --background-activated: var(--color-secondary-red-lightest);
  }

  .tertiary {
    --background: var(--color-base-white);
    --border-color: var(--color-secondary-paper-dark);
    color: var(--color-base-black);
    --background-activated: var(--color-secondary-paper-light);
  }

  .ghost {
    color: v-bind(colorGhost);
    --border-style: none;
    --background-activated: var(--color-base-white);

    &:active {
      text-decoration: underline;
    }
  }

  .dark-secondary {
    color: var(--color-base-white);
    --border-color: var(--color-base-white);
    --background-activated: var(--color-base-white);

    &:active {
      color: var(--color-base-green);
    }
  }

  .dark-primary {
    --background: var(--color-base-white);
    --border-style: none;
    color: var(--color-base-black);
    --background-activated: var(--color-secondary-green-light);
  }
</style>
